/* eslint-disable max-lines */
import _ from 'lodash';
import usePrevious from 'components/hooks/usePrevious';
import { Grid } from '@material-ui/core';
import AppRouter from 'common/AppRouter';
import { TRACKING, TEAM_TYPES } from 'common/constants/constants';
import { SPACES } from 'common/constants/sizes';
import * as TrackingService from 'common/services/tracking.service';
import { useEffect, useState } from 'react';

import { ACCOUNT_TYPES_MAP, FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { getTokens } from 'common/services/api-tokens.service';
import { DefinitionLevels } from 'common/services/settings.service';
import TabList from 'components/TabList';
import RestrictedSettingRoute from 'components/base/RestrictedSettingRoute';
import Text from 'components/base/Text';
import {
  GrayBackground,
  Padding,
  Page,
  WhiteSection
} from 'components/base/layout-components';
import useSettingsService from 'components/hooks/useSettingsService';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import QueryString from 'query-string';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { isPostOnboardingWithoutReposSelector } from 'state/selectors/flows';
import { isPmToolAzureBoardsSelector } from 'state/selectors/sensors-authorizations';
import { settingsSelector } from 'state/selectors/settings';
import { isSelectedTeamParentSelector } from 'state/selectors/teams';
import styled from 'styled-components';
import useDoraFeatureFlags from '../../hooks/useDoraFeatureFlags';
import ApiTokens from './views/ApiTokens';
import ContributorsAndTeamsRouter from './views/ContributorsAndTeams/ContributorsAndTeamsRouter';
import General from './views/General';
import GitSettings from './views/GitSettings/GitSettings';
import MsTeams from './views/MicrosoftTeams';
import Notifications from './views/Notifications';
import ProjectManagement from './views/ProjectManagement';
import { managementToolView } from './views/ProjectManagement/services/project-management.service';
import { NoReposAfterOnboarding } from './views/RepositoriesV2/repositoriesSections';
import SystemSettings from './views/Settings';
import Slack from './views/Slack';
import TeamInsightsSettingsContainer from './views/InsightsSettings/TeamInsightsSettingsContainer';
import UsersRouter from './views/Users/UsersRouter';
import AuthenticationSettingRouter from './views/Authentication/AuthenticationSettingRouter';
import useAuthenticationSettingTabs from './views/Authentication/useAuthenticationSettingTabs';
import GitTeamSettings from './views/GitSettings/GitTeamSettings/GitTeamSettings';
import { myAccountSelector } from 'state/selectors/account';
import { migrationStaleMessage } from 'components/MigrationStaleBanner';

const StyledBackground = styled(GrayBackground)`
  padding: ${SPACES.small};
`;

const FullHeightGrid = styled(Grid)`
  height: 100%;
`;
/* eslint-disable max-lines-per-function */
const Settings = ({
  toggleModal,
  uiState,
  selectedTeam = {},
  isTeamLicense,
  myAccount = {},
  loadSettings
}) => {
  const account = useSelector(myAccountSelector);
  // in order to distinguish between previousSelectedTeamId = undefined because of first time
  // and previousSelectedTeamId = undefined because of All Teams,
  // we set empty selectedTeam?.id to -1
  const selectedTeamId = selectedTeam?.id || -1;
  const previousSelectedTeamId = usePrevious(selectedTeamId);
  const history = useHistory();
  const isGroup = useSelector(isSelectedTeamParentSelector);
  const authenticationTabs = useAuthenticationSettingTabs();

  const isPostOnboardingWithoutRepos = useSelector(
    isPostOnboardingWithoutReposSelector
  );
  const isPmToolAzureBoards = useSelector(isPmToolAzureBoardsSelector);

  const [apiTokens, setApiTokens] = useState([]);

  useEffect(() => {
    const fetchTokens = async () => {
      try {
        const response = await getTokens();
        setApiTokens(get(response, 'tokens', []));
      } catch (exp) {
        setApiTokens([]);
      }
      await loadSettings();
    };
    fetchTokens();
  }, []);

  const allSettings = useSelector(settingsSelector);
  const { getSettingOrDefinitionValue } = useSettingsService();

  const isAdmin = myAccount.type_id === ACCOUNT_TYPES_MAP.Primary;
  const isSuperUser = myAccount.type_id === ACCOUNT_TYPES_MAP.SuperUser;

  const onTabClick = ({ path }) => history.push(path);

  const isAzureBoardsTeamSettingsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isAzureBoardsTeamSettingsEnabled
  });
  const { isAllControbutorsTeamSelected } = useDoraFeatureFlags();
  const qs = QueryString.parse(history.location.search);

  const isMsTeamsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.msTeamsEnabled
  });
  const isApiTokensEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.apiTokens
  });
  const isRBACEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isRBACEnabled
  });
  const isSharedDemoEnvironmentFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isSharedDemoEnvironment
  });
  const isTeamGitSettingEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isTeamGitSettingEnabled
  });
  const isInsightsTeamSettingsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isInsightsTeamSettingsEnabled
  });
  const newUserManagementEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.newUserManagementEnabled
  });

  const isShowExpediaMigrationStaleMode = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.showExpediaMigrationStaleMode
  });

  const shouldShowUsers =
    !isSharedDemoEnvironmentFF ||
    (isSharedDemoEnvironmentFF && isSuperUser) ||
    (isSharedDemoEnvironmentFF && account?.email?.endsWith('@linearb.io'));

  // when RBAC is open - only admin and super user can access
  // TO DO: handle RBAC in free dora isTeamLicense=true
  const permissions =
    isRBACEnabledFF && !isTeamLicense ? isAdmin || isSuperUser : true;

  const canAccessAllCompanySettings = isRBACEnabledFF
    ? isAdmin || isSuperUser
    : true;

  const isAllTeams =
    uiState.selectedTeamId === null ||
    uiState.selectedTeamId === undefined ||
    selectedTeam.type === TEAM_TYPES.ALL_TEAMS;

  const defaultFirstTab =
    isGroup && !isAllTeams
      ? AppRouter.PROJECT_MANAGEMENT.path
      : canAccessAllCompanySettings
      ? AppRouter.GENERAL.path
      : isTeamLicense
      ? AppRouter.CONTRIBUTORS_SETTINGS.path
      : AppRouter.TEAMS_SETTINGS.path;

  const isOrgSettingsQs = Boolean(qs.isOrgSettings) === true;
  const isOrgSettings = isAllTeams || isOrgSettingsQs;

  let tabs = isOrgSettings
    ? []
    : [
        ...(isGroup ? [] : [{ name: 'General', path: '/settings/general' }]),
        ...(isTeamGitSettingEnabled &&
        !isGroup &&
        !isAllControbutorsTeamSelected
          ? [
              {
                name: 'Git',
                path: `${AppRouter.REPOSITORIES.path}`,
                selectedTabPath: `${AppRouter.GIT_SETTINGS.path}`
              }
            ]
          : []),
        ...((isPmToolAzureBoards && !isAzureBoardsTeamSettingsEnabled) ||
        isAllControbutorsTeamSelected
          ? []
          : [
              {
                name: 'Project Management',
                path: AppRouter.PROJECT_MANAGEMENT.path,
                ...(isShowExpediaMigrationStaleMode
                  ? { disabled: true, tooltip: migrationStaleMessage }
                  : {})
              }
            ])
      ];

  if (isOrgSettings && permissions) {
    tabs.push({
      name: 'General',
      path: '/settings/general?isOrgSettings=true'
    });
    tabs.push({
      name: 'Git',
      path: `${AppRouter.REPOSITORIES.path}?isOrgSettings=true`,
      selectedTabPath: `${AppRouter.GIT_SETTINGS.path}?isOrgSettings=true`
    });

    tabs.push({
      name: 'Project Management',
      path: `${AppRouter.PROJECT_MANAGEMENT.path}?isOrgSettings=true`,
      ...(isShowExpediaMigrationStaleMode
        ? { disabled: true, tooltip: migrationStaleMessage }
        : {})
    });

    if (shouldShowUsers) {
      tabs.push({
        name: 'Users',
        path: `${AppRouter.USERS.path}?isOrgSettings=true`
      });
    }
  }

  if (!isAllControbutorsTeamSelected && !isOrgSettings && !isGroup) {
    tabs.push({
      name: 'Goals',
      path: '/settings/notifications'
    });
  }

  const isOrgSettingsWithoutTeamLicense = isOrgSettings && !isTeamLicense;
  const shouldShowSlackUnderTeamSettings =
    !isOrgSettings && !isGroup && !isAllControbutorsTeamSelected;
  const shouldShowSlackUnderCompanySettings =
    isOrgSettingsWithoutTeamLicense && permissions;

  const shouldShowMsTeamsTab = isMsTeamsEnabled;
  if (shouldShowSlackUnderCompanySettings || shouldShowSlackUnderTeamSettings) {
    tabs.push({
      name: 'Slack',
      path: `/settings/slack${isOrgSettings ? '?isOrgSettings=true' : ''}`
    });

    if (shouldShowMsTeamsTab && (!isGroup || isOrgSettings)) {
      tabs.push({
        name: 'Microsoft Teams',
        path: `${AppRouter.MSTEAMS.path}${
          isOrgSettings ? '?isOrgSettings=true' : ''
        }`
      });
    }
  }

  if (
    (shouldShowSlackUnderTeamSettings || shouldShowMsTeamsTab) &&
    !isAllControbutorsTeamSelected &&
    !isOrgSettings &&
    !isGroup &&
    isInsightsTeamSettingsEnabled
  ) {
    tabs.push({
      name: 'Insights',
      path: `/settings/teaminsights${
        isOrgSettings ? '?isOrgSettings=true' : ''
      }`
    });
  }

  if (isOrgSettings) {
    const legacyName = !isTeamLicense ? 'Teams & Contributors' : 'Contributors';
    const legacyPath = `${
      !isTeamLicense
        ? AppRouter.TEAMS_SETTINGS.path
        : AppRouter.CONTRIBUTORS_SETTINGS.path
    }?isOrgSettings=true`;

    tabs.push({
      name: newUserManagementEnabled ? 'People & Teams' : legacyName,
      path: newUserManagementEnabled
        ? `${AppRouter.USER_SETTINGS.path}?isOrgSettings=true`
        : legacyPath,
      selectedTabPath: AppRouter.CONTRIBUTORS_AND_TEAMS_SETTINGS.path
    });
  }

  if (
    isApiTokensEnabled &&
    isOrgSettings &&
    (isRBACEnabledFF ? isAdmin || isSuperUser : isAdmin || apiTokens.length > 0)
  ) {
    tabs.push({
      name: 'API Tokens',
      path: `${AppRouter.API_TOKENS.path}?isOrgSettings=true`
    });
  }

  const shouldShowAuthenticationSetting =
    authenticationTabs.length > 0 &&
    isOrgSettings &&
    (isAdmin || isSuperUser) &&
    !isTeamLicense;

  if (shouldShowAuthenticationSetting) {
    const authenticationTabName =
      authenticationTabs.length === 1
        ? authenticationTabs[0].name
        : 'Authentication';

    tabs.push({
      name: authenticationTabName,
      path: `${AppRouter.AUTHENTICATION_SETTINGS.path}?isOrgSettings=true`
    });
  }
  if (isOrgSettings && permissions) {
    tabs.push({
      name: 'Advanced',
      path: '/settings/organization?isOrgSettings=true'
    });
  }

  const selectedTab =
    tabs.find(({ path, selectedTabPath }) =>
      QueryString.parseUrl(history.location.pathname).url.includes(
        QueryString.parseUrl(selectedTabPath ? selectedTabPath : path).url
      )
    ) || tabs[0];

  useEffect(() => {
    if (
      !!previousSelectedTeamId &&
      !_.isEqual(previousSelectedTeamId, selectedTeamId)
    ) {
      const path = history.location.pathname;

      let search = QueryString.parse(window.location.search, {
        ignoreQueryPrefix: true
      });

      const { redirectToPath } = search;

      search = _.omit(search, ['redirectToPath']);

      if (!isAllTeams && !isTeamLicense) {
        search = _.omit(search, ['isOrgSettings']);
      }

      const newSearchString = `?${QueryString.stringify(search)}`;

      // When changing selectedTeam, go to first tab
      // except if redirectToPath is true
      if (redirectToPath) {
        history.push({
          pathname: path,
          search: newSearchString
        });
      } else {
        history.push({
          pathname: defaultFirstTab,
          search: newSearchString
        });
      }
    }
  }, [selectedTeamId]);

  const visibleSettings = orderBy(allSettings, 'viewOrder')
    .filter(
      ({ isVisible, definitionLevel }) =>
        isVisible && definitionLevel === DefinitionLevels.ORGANIZATION
    )
    .map(setting => ({
      ...setting,
      definitionId: setting.id,
      value: getSettingOrDefinitionValue({ name: setting.name })
    }));
  if (isPostOnboardingWithoutRepos) {
    tabs = [{ name: 'Git', path: AppRouter.REPOSITORIES.path }];
  }

  const defaultPath = AppRouter.TEAMS_SETTINGS.path;

  return (
    <>
      {isPostOnboardingWithoutRepos && (
        <Padding l='medium' r='small' t='small'>
          <NoReposAfterOnboarding />
        </Padding>
      )}
      <Page>
        <StyledBackground>
          <WhiteSection padding={SPACES.medium} height='100%'>
            <Grid item xs={12}>
              <Text size='x-x-x-large' weight='300' pb={SPACES.small}>
                {isOrgSettings
                  ? 'Company Settings'
                  : isGroup
                  ? `Group Settings - ${selectedTeam && selectedTeam.name}`
                  : `Team Settings - ${selectedTeam && selectedTeam.name}`}
              </Text>
              <Text size='medium' color='shuttleGray' pb={SPACES.medium}>
                {isOrgSettings
                  ? 'These are the default settings for the company. Team settings override them.'
                  : isGroup
                  ? `Group settings will override the general company settings for this group`
                  : `Team settings will override the general company settings for this team`}
              </Text>
              <TabList
                display='inSpacedSection'
                tabs={tabs}
                onTabClick={onTabClick}
                selectedTab={selectedTab ? selectedTab.name : ''}
              />
            </Grid>
            <FullHeightGrid container>
              <Grid item xs={12}>
                <Switch>
                  <Route
                    path={AppRouter.NOTIFICATIONS.path}
                    render={() => <Notifications />}
                  />
                  <Route
                    path={AppRouter.CONTRIBUTORS_AND_TEAMS_SETTINGS.path}
                    component={ContributorsAndTeamsRouter}
                  />
                  {/* restricted routes when RBACEnabled = true */}
                  <RestrictedSettingRoute
                    isOrgSettings={isOrgSettings}
                    isOrgRestricted={!permissions}
                    history={history}
                    defaultPath={defaultPath}
                  >
                    <>
                      <Route
                        path={AppRouter.CONFIGURATION.path}
                        render={() => (
                          <SystemSettings
                            apiTokens={apiTokens}
                            setApiTokens={setApiTokens}
                            onMount={() =>
                              TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
                                page: TRACKING.PAGES.SETTINGS,
                                page_tab: TRACKING.PAGE_TABS.CONFIGURE
                              })
                            }
                            settings={visibleSettings}
                            entityType='organization'
                            toggleModal={toggleModal}
                          />
                        )}
                      />
                      <Route
                        path={AppRouter.GIT_SETTINGS.path}
                        render={() =>
                          isOrgSettings ? (
                            <GitSettings
                              apiTokens={apiTokens}
                              setApiTokens={setApiTokens}
                            />
                          ) : (
                            <GitTeamSettings
                              apiTokens={apiTokens}
                              setApiTokens={setApiTokens}
                            />
                          )
                        }
                      />

                      <Route
                        path={AppRouter.API_TOKENS.path}
                        render={() => (
                          <ApiTokens
                            apiTokens={apiTokens}
                            setApiTokens={setApiTokens}
                            allSettings={allSettings}
                          />
                        )}
                      />
                      {shouldShowAuthenticationSetting && (
                        <Route
                          path={AppRouter.AUTHENTICATION_SETTINGS.path}
                          render={() => <AuthenticationSettingRouter />}
                        />
                      )}
                      {shouldShowUsers && (
                        <Route
                          path={AppRouter.USERS.path}
                          render={() => <UsersRouter history={history} />}
                        />
                      )}
                      <Route
                        path={AppRouter.GENERAL.path}
                        render={() => (
                          <General
                            selectedTeam={selectedTeam}
                            isOrgSettings={isOrgSettings}
                          />
                        )}
                      />
                      <Route
                        path={AppRouter.PROJECT_MANAGEMENT_TOOLS.path}
                        render={() => (
                          <ProjectManagement
                            history={history}
                            selectedTeam={selectedTeam}
                            isOrgSettings={isOrgSettings}
                            apiTokens={apiTokens}
                            setApiTokens={setApiTokens}
                            managementToolView={managementToolView(
                              window.location.pathname
                            )}
                          />
                        )}
                      />
                      <Route
                        path={AppRouter.SLACK.path}
                        render={() => (
                          <Slack
                            isOrgSettings={isOrgSettings}
                            history={history}
                          />
                        )}
                      />
                      <Route
                        path={AppRouter.MSTEAMS.path}
                        render={() => (
                          <MsTeams
                            isOrgSettings={isOrgSettings}
                            history={history}
                          />
                        )}
                      />
                      <Route
                        path={AppRouter.TEAM_INSIGHTS.path}
                        render={() => (
                          <TeamInsightsSettingsContainer
                            selectedTeam={selectedTeam}
                            isOrgSettings={isOrgSettings}
                            history={history}
                          />
                        )}
                      />
                    </>
                  </RestrictedSettingRoute>
                </Switch>
              </Grid>
            </FullHeightGrid>
          </WhiteSection>
        </StyledBackground>
      </Page>
    </>
  );
};

export default Settings;
